import { useEffect, useState } from "react";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import { fetchedAllPaymentsByOperation } from "../../../apis/apis.accounting/apisCash";

const filename = "Reporte_estado_de_cuentas";

// ── helpers de formato ──────────────────────────────────────────────────────
const money = (val) => `S/ ${Number(val || 0).toFixed(2)}`;

// ── clases base reutilizables ───────────────────────────────────────────────
const thBase =
    "px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600";
const tdBase =
    "px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle";
const tdMoney = `${tdBase} text-right font-semibold`;

// Colores por columna (header / celda)
const COL = {
    blue:   { th: `${thBase} bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 border-l-2 border-blue-300 dark:border-blue-600`,   td: `${tdMoney} bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 border-l-2 border-blue-200 dark:border-blue-700` },
    pink:   { th: `${thBase} bg-pink-100 dark:bg-pink-900/40 text-pink-800 dark:text-pink-200 border-l-2 border-pink-300 dark:border-pink-600`,   td: `${tdMoney} bg-pink-50 dark:bg-pink-900/20 text-pink-800 dark:text-pink-200 border-l-2 border-pink-200 dark:border-pink-700` },
    orange: { th: `${thBase} bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200 border-l-2 border-orange-300 dark:border-orange-600`, td: `${tdMoney} bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200 border-l-2 border-orange-200 dark:border-orange-700` },
    purple: { th: `${thBase} bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200 border-l-2 border-purple-300 dark:border-purple-600`, td: `${tdMoney} bg-purple-50 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200 border-l-2 border-purple-200 dark:border-purple-700` },
    green:  { th: `${thBase} bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 border-l-2 border-emerald-300 dark:border-emerald-600`, td: `${tdMoney} bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200 border-l-2 border-emerald-200 dark:border-emerald-700` },
};

function pendingTd(val) {
    const hasPending = Number(val) > 0;
    return hasPending
        ? `${tdMoney} bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-l-2 border-red-300 dark:border-red-700`
        : `${tdMoney} bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-l-2 border-amber-200 dark:border-amber-700`;
}

function pendingTh(val) {
    const hasPending = Number(val) > 0;
    return hasPending
        ? `${thBase} bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 border-l-2 border-red-300 dark:border-red-600`
        : `${thBase} bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-l-2 border-amber-300 dark:border-amber-600`;
}

function rowClass(item) {
    if (item.operationStatus === "03")
        return "opacity-60 bg-red-50 dark:bg-red-900/10";
    if (item.operationType === "01")
        return "bg-amber-50 dark:bg-amber-900/10";
    return "hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors";
}

export function AccountReceiveList({ sales, setCashFlow, setCashFlowSet, setIsModalOpen }) {
    const [summary, setSummary] = useState({
        sumTotalPurchased: 0,
        sumTotalPaid: 0,
        sumTotalReturned: 0,
        sumTotalPending: 0,
        sumTotalPaidInCash: 0,
        sumTotalPaidInYape: 0,
        sumTotalInCredit: 0,
    });

    useEffect(() => {
        const active = sales?.filter((i) => i.operationStatus === "02") ?? [];
        const activeOrReturned = sales?.filter(
            (i) => i.operationStatus === "02" || i.operationStatus === "03"
        ) ?? [];

        const sum = (arr, key) =>
            arr.reduce((t, o) => t + Number(o[key] || 0), 0);

        setSummary({
            sumTotalPurchased:  sum(active, "totalPurchased"),
            sumTotalPaid:       sum(active, "totalPaid"),
            sumTotalReturned:   sum(activeOrReturned, "totalReturned"),
            sumTotalPending:    sum(active, "totalPending"),
            sumTotalPaidInCash: sum(active, "totalPaidInCash"),
            sumTotalPaidInYape: sum(active, "totalPaidInYape"),
            sumTotalInCredit:   sum(active, "totalInCredit"),
        });
    }, [sales]);

    const getAllPayments = async (operationID) => {
        try {
            const response = await fetchedAllPaymentsByOperation({ operationID });
            setCashFlowSet(response);
        } catch (error) {
            throw error;
        }
    };

    const handlePayClick = (item) => {
        setCashFlow((prev) => ({
            ...prev,
            description: "",
            type: "E",
            operationID: Number(item.operationID),
            total: Number(item.totalInCredit),
            originalPending: Number(item.totalInCredit || item.totalPending),
        }));
        getAllPayments(item.operationID);
        setIsModalOpen(true);
    };

    const hasPending = (item) =>
        Number(item.totalInCredit) > 0 || Number(item.totalPending) > 0;

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">

            {/* Card header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                        Detalle de operaciones
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {sales?.length ?? 0} registros encontrados
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Leyenda */}
                    <div className="hidden md:flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-blue-300 dark:bg-blue-600"></span>Comprado</span>
                        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-pink-300 dark:bg-pink-600"></span>Crédito</span>
                        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-300 dark:bg-emerald-600"></span>Efectivo</span>
                        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-red-300 dark:bg-red-600"></span>Saldo</span>
                    </div>
                    <ReactHTMLTableToExcel
                        id="table-xls-button"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-xs font-medium px-3 py-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                        table="table-to-xls"
                        filename={filename}
                        sheet="report"
                        buttonText="↓ Exportar XLS"
                    />
                </div>
            </div>

            {/* ── Desktop table ── */}
            <div className="hidden md:block overflow-x-auto max-h-[70vh] overflow-y-auto bg-white dark:bg-gray-900">
                <table
                    className="min-w-full text-sm border-separate border-spacing-0"
                    id="table-to-xls"
                >
                    <thead className="sticky top-0 z-10">
                        <tr>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>ID</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Documento</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Fecha / Hora</th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>Usuario</th>
                            <th className={COL.blue.th}>
                                <i className="fas fa-shopping-cart mr-1"></i>Comprado
                            </th>
                            <th className={COL.pink.th}>
                                <i className="fas fa-credit-card mr-1"></i>Crédito
                            </th>
                            <th className={COL.orange.th}>
                                <i className="fas fa-mobile-alt mr-1"></i>Yape
                            </th>
                            <th className={COL.purple.th}>
                                <i className="fas fa-undo mr-1"></i>Devuelto
                            </th>
                            <th className={COL.green.th}>
                                <i className="fas fa-money-bill-wave mr-1"></i>Efectivo
                            </th>
                            <th className={pendingTh(summary.sumTotalPending)}>
                                <i className="fas fa-balance-scale mr-1"></i>Saldo
                            </th>
                            <th className={`${thBase} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}></th>
                        </tr>
                    </thead>

                    <tbody>
                        {sales?.map((item, index) => (
                            <tr key={index} className={rowClass(item)}>
                                <td className={`${tdBase} text-blue-600 dark:text-blue-400 font-medium`}>
                                    {item.operationID}
                                </td>
                                <td className={`${tdBase} font-semibold text-gray-800 dark:text-gray-200`}>
                                    {item.receiptSerial} - {item.receiptNumber}
                                </td>
                                <td className={`${tdBase} text-gray-600 dark:text-gray-400`}>
                                    {item.registerDate} {item.registerTime}
                                </td>
                                <td className={`${tdBase} text-gray-600 dark:text-gray-400`}>
                                    {item.userName?.substring(0, 5)}...
                                </td>
                                <td className={COL.blue.td}>{money(item.totalPurchased)}</td>
                                <td className={COL.pink.td}>{money(item.totalInCredit)}</td>
                                <td className={COL.orange.td}>{money(item.totalPaidInYape)}</td>
                                <td className={COL.purple.td}>{money(item.totalReturned)}</td>
                                <td className={COL.green.td}>{money(item.totalPaidInCash)}</td>
                                <td className={pendingTd(item.totalPending)}>{money(item.totalPending)}</td>
                                <td className={`${tdBase} text-center`}>
                                    {item.operationStatus === "02" && (
                                        <button
                                            type="button"
                                            onClick={() => handlePayClick(item)}
                                            className={
                                                hasPending(item)
                                                    ? "inline-flex items-center gap-1 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-2.5 py-1 transition-colors"
                                                    : "inline-flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs font-medium px-2.5 py-1 transition-colors"
                                            }
                                        >
                                            {hasPending(item) ? "PAGAR" : "VER"}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}

                        {(!sales || sales.length === 0) && (
                            <tr>
                                <td colSpan={11} className="py-12 text-center text-sm text-gray-400 dark:text-gray-500">
                                    No hay operaciones para mostrar
                                </td>
                            </tr>
                        )}
                    </tbody>

                    {/* Totals footer */}
                    <tfoot>
                        <tr className="bg-gray-800 dark:bg-gray-950">
                            <td
                                colSpan={4}
                                className="px-3 py-2 text-xs font-bold text-right text-white uppercase tracking-wide border-t border-gray-600"
                            >
                                <i className="fas fa-calculator mr-2"></i>TOTAL
                            </td>
                            <td className={`${tdMoney} bg-blue-900/60 text-blue-200 border-l-2 border-blue-600 border-t border-gray-600`}>
                                {money(summary.sumTotalPurchased)}
                            </td>
                            <td className={`${tdMoney} bg-pink-900/60 text-pink-200 border-l-2 border-pink-600 border-t border-gray-600`}>
                                {money(summary.sumTotalInCredit)}
                            </td>
                            <td className={`${tdMoney} bg-orange-900/60 text-orange-200 border-l-2 border-orange-600 border-t border-gray-600`}>
                                {money(summary.sumTotalPaidInYape)}
                            </td>
                            <td className={`${tdMoney} bg-purple-900/60 text-purple-200 border-l-2 border-purple-600 border-t border-gray-600`}>
                                {money(summary.sumTotalReturned)}
                            </td>
                            <td className={`${tdMoney} bg-emerald-900/60 text-emerald-200 border-l-2 border-emerald-600 border-t border-gray-600`}>
                                {money(summary.sumTotalPaidInCash)}
                            </td>
                            <td
                                className={`${tdMoney} border-t border-gray-600 border-l-2 ${
                                    Number(summary.sumTotalPending) > 0
                                        ? "bg-red-900/60 text-red-200 border-red-600"
                                        : "bg-amber-900/60 text-amber-200 border-amber-600"
                                }`}
                            >
                                {money(summary.sumTotalPending)}
                            </td>
                            <td className="border-t border-gray-600 bg-gray-800 dark:bg-gray-950"></td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {/* ── Mobile cards ── */}
            <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700">
                {sales?.map((item, index) => (
                    <div key={index} className={`p-4 ${rowClass(item)}`}>
                        {/* Card header */}
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                    <i className="fas fa-receipt mr-1 text-gray-400"></i>
                                    {item.receiptSerial} - {item.receiptNumber}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {item.registerDate} {item.registerTime} · {item.userName}
                                </p>
                            </div>
                            <span
                                className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                    item.operationStatus === "03"
                                        ? "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"
                                        : item.operationType === "01"
                                          ? "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                                          : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                                }`}
                            >
                                {item.operationStatus === "03"
                                    ? "ANULADO"
                                    : item.operationType === "01"
                                      ? "COTIZACIÓN"
                                      : "COMPROBANTE"}
                            </span>
                        </div>

                        {/* Amounts grid */}
                        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                            {[
                                { label: "Comprado", val: item.totalPurchased, color: "text-blue-600 dark:text-blue-400" },
                                { label: "Crédito",  val: item.totalInCredit,  color: "text-pink-600 dark:text-pink-400" },
                                { label: "Yape",     val: item.totalPaidInYape, color: "text-orange-600 dark:text-orange-400" },
                                { label: "Devuelto", val: item.totalReturned,  color: "text-purple-600 dark:text-purple-400" },
                                { label: "Efectivo", val: item.totalPaidInCash, color: "text-emerald-600 dark:text-emerald-400" },
                                {
                                    label: "Saldo",
                                    val: item.totalPending,
                                    color: Number(item.totalPending) > 0
                                        ? "text-red-600 dark:text-red-400 font-bold"
                                        : "text-amber-600 dark:text-amber-400",
                                },
                            ].map(({ label, val, color }) => (
                                <div key={label} className="flex justify-between items-center bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-1.5">
                                    <span className="text-gray-500 dark:text-gray-400">{label}</span>
                                    <span className={`font-semibold ${color}`}>{money(val)}</span>
                                </div>
                            ))}
                        </div>

                        {/* Action */}
                        {item.operationStatus === "02" && (
                            <button
                                type="button"
                                onClick={() => handlePayClick(item)}
                                className={`w-full rounded-lg text-sm font-medium py-2 transition-colors ${
                                    hasPending(item)
                                        ? "bg-red-600 hover:bg-red-700 text-white"
                                        : "border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                }`}
                            >
                                <i className={`fas ${hasPending(item) ? "fa-credit-card" : "fa-eye"} mr-2`}></i>
                                {hasPending(item) ? "PAGAR" : "VER DETALLES"}
                            </button>
                        )}
                    </div>
                ))}

                {/* Mobile totals */}
                {sales && sales.length > 0 && (
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50">
                        <p className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
                            <i className="fas fa-calculator mr-2"></i>Resumen de totales
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            {[
                                { label: "Total Comprado", val: summary.sumTotalPurchased, color: "text-blue-600 dark:text-blue-400" },
                                { label: "Total Crédito",  val: summary.sumTotalInCredit,  color: "text-pink-600 dark:text-pink-400" },
                                { label: "Total Yape",     val: summary.sumTotalPaidInYape, color: "text-orange-600 dark:text-orange-400" },
                                { label: "Total Devuelto", val: summary.sumTotalReturned,  color: "text-purple-600 dark:text-purple-400" },
                                { label: "Total Efectivo", val: summary.sumTotalPaidInCash, color: "text-emerald-600 dark:text-emerald-400" },
                                {
                                    label: "Saldo Total",
                                    val: summary.sumTotalPending,
                                    color: Number(summary.sumTotalPending) > 0
                                        ? "text-red-600 dark:text-red-400 font-bold"
                                        : "text-amber-600 dark:text-amber-400",
                                },
                            ].map(({ label, val, color }) => (
                                <div key={label} className="flex justify-between items-center bg-white dark:bg-gray-700 rounded-lg px-3 py-1.5 border border-gray-200 dark:border-gray-600">
                                    <span className="text-gray-500 dark:text-gray-400">{label}</span>
                                    <span className={`font-semibold ${color}`}>{money(val)}</span>
                                </div>
                            ))}
                        </div>
                        {Number(summary.sumTotalPending) > 0 && (
                            <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                                <i className="fas fa-exclamation-triangle"></i>
                                <span className="font-medium">Hay saldos pendientes por cobrar</span>
                            </div>
                        )}
                    </div>
                )}

                {(!sales || sales.length === 0) && (
                    <div className="py-12 text-center text-sm text-gray-400 dark:text-gray-500">
                        No hay operaciones para mostrar
                    </div>
                )}
            </div>
        </div>
    );
}
